/**
 * Countries, E.164 prefixes and languages — the complete ITU list.
 *
 * 76 languages, over 230 territories with an allocated country code. The full
 * list, not a selection: the platform is used by a telecom operator who tests
 * anywhere, and a curated list would mean the first unforeseen destination has
 * to be typed in by hand, with the prefix guessed.
 *
 * The practical role: to make it impossible for a silent mismatch to remain
 * between the campaign country and the prefix guard. A campaign with country GB
 * and prefix 44 into which you load Romanian numbers rejects the whole batch,
 * without anything appearing broken.
 *
 * Zone 1 (the North American plan) appears with the extended prefixes of the
 * Caribbean territories — 1242 Bahamas, 1246 Barbados and so on — because for
 * testing what matters is the real destination, not the fact that it shares a
 * country code with the United States.
 *
 * The language next to each country is the primary one, used only as the
 * DEFAULT value when creating a campaign. It stays editable: Belgium is nl+fr,
 * Switzerland de+fr+it, and the operator may have reasons to call in English
 * anywhere.
 */
/** Ordered by name, so the list in the interface reads alphabetically. */
export const COUNTRIES = [
    { code: 'AF', name: 'Afghanistan', prefix: '93', lang: 'fa-IR' },
    { code: 'AL', name: 'Albania', prefix: '355', lang: 'sq-AL' },
    { code: 'DZ', name: 'Algeria', prefix: '213', lang: 'ar-DZ' },
    { code: 'AS', name: 'American Samoa', prefix: '1684', lang: 'en-US' },
    { code: 'AD', name: 'Andorra', prefix: '376', lang: 'es-ES' },
    { code: 'AO', name: 'Angola', prefix: '244', lang: 'pt-PT' },
    { code: 'AI', name: 'Anguilla', prefix: '1264', lang: 'en-GB' },
    { code: 'AG', name: 'Antigua and Barbuda', prefix: '1268', lang: 'en-GB' },
    { code: 'AR', name: 'Argentina', prefix: '54', lang: 'es-AR' },
    { code: 'AM', name: 'Armenia', prefix: '374', lang: 'hy-AM' },
    { code: 'AW', name: 'Aruba', prefix: '297', lang: 'nl-NL' },
    { code: 'AU', name: 'Australia', prefix: '61', lang: 'en-AU' },
    { code: 'AT', name: 'Austria', prefix: '43', lang: 'de-AT' },
    { code: 'AZ', name: 'Azerbaijan', prefix: '994', lang: 'az-AZ' },
    { code: 'BS', name: 'Bahamas', prefix: '1242', lang: 'en-US' },
    { code: 'BH', name: 'Bahrain', prefix: '973', lang: 'ar-SA' },
    { code: 'BD', name: 'Bangladesh', prefix: '880', lang: 'bn-BD' },
    { code: 'BB', name: 'Barbados', prefix: '1246', lang: 'en-US' },
    { code: 'BY', name: 'Belarus', prefix: '375', lang: 'ru-RU' },
    { code: 'BE', name: 'Belgium', prefix: '32', lang: 'nl-BE' },
    { code: 'BZ', name: 'Belize', prefix: '501', lang: 'en-GB' },
    { code: 'BJ', name: 'Benin', prefix: '229', lang: 'fr-FR' },
    { code: 'BM', name: 'Bermuda', prefix: '1441', lang: 'en-GB' },
    { code: 'BT', name: 'Bhutan', prefix: '975', lang: 'en-GB' },
    { code: 'BO', name: 'Bolivia', prefix: '591', lang: 'es-ES' },
    { code: 'BA', name: 'Bosnia and Herzegovina', prefix: '387', lang: 'hr-HR' },
    { code: 'BW', name: 'Botswana', prefix: '267', lang: 'en-GB' },
    { code: 'BR', name: 'Brazil', prefix: '55', lang: 'pt-BR' },
    { code: 'IO', name: 'British Indian Ocean Territory', prefix: '246', lang: 'en-GB' },
    { code: 'VG', name: 'British Virgin Islands', prefix: '1284', lang: 'en-GB' },
    { code: 'BN', name: 'Brunei', prefix: '673', lang: 'ms-MY' },
    { code: 'BG', name: 'Bulgaria', prefix: '359', lang: 'bg-BG' },
    { code: 'BF', name: 'Burkina Faso', prefix: '226', lang: 'fr-FR' },
    { code: 'BI', name: 'Burundi', prefix: '257', lang: 'fr-FR' },
    { code: 'KH', name: 'Cambodia', prefix: '855', lang: 'km-KH' },
    { code: 'CM', name: 'Cameroon', prefix: '237', lang: 'fr-FR' },
    { code: 'CA', name: 'Canada', prefix: '1', lang: 'en-CA' },
    { code: 'CV', name: 'Cape Verde', prefix: '238', lang: 'pt-PT' },
    { code: 'KY', name: 'Cayman Islands', prefix: '1345', lang: 'en-GB' },
    { code: 'CF', name: 'Central African Republic', prefix: '236', lang: 'fr-FR' },
    { code: 'TD', name: 'Chad', prefix: '235', lang: 'fr-FR' },
    { code: 'CL', name: 'Chile', prefix: '56', lang: 'es-ES' },
    { code: 'CN', name: 'China', prefix: '86', lang: 'zh-CN' },
    { code: 'CO', name: 'Colombia', prefix: '57', lang: 'es-ES' },
    { code: 'KM', name: 'Comoros', prefix: '269', lang: 'fr-FR' },
    { code: 'CG', name: 'Congo', prefix: '242', lang: 'fr-FR' },
    { code: 'CD', name: 'Congo (DR)', prefix: '243', lang: 'fr-FR' },
    { code: 'CK', name: 'Cook Islands', prefix: '682', lang: 'en-NZ' },
    { code: 'CR', name: 'Costa Rica', prefix: '506', lang: 'es-ES' },
    { code: 'CI', name: "Côte d'Ivoire", prefix: '225', lang: 'fr-FR' },
    { code: 'HR', name: 'Croatia', prefix: '385', lang: 'hr-HR' },
    { code: 'CU', name: 'Cuba', prefix: '53', lang: 'es-ES' },
    { code: 'CW', name: 'Curaçao', prefix: '599', lang: 'nl-NL' },
    { code: 'CY', name: 'Cyprus', prefix: '357', lang: 'el-GR' },
    { code: 'CZ', name: 'Czechia', prefix: '420', lang: 'cs-CZ' },
    { code: 'DK', name: 'Denmark', prefix: '45', lang: 'da-DK' },
    { code: 'DJ', name: 'Djibouti', prefix: '253', lang: 'fr-FR' },
    { code: 'DM', name: 'Dominica', prefix: '1767', lang: 'en-GB' },
    { code: 'DO', name: 'Dominican Republic', prefix: '1809', lang: 'es-ES' },
    { code: 'EC', name: 'Ecuador', prefix: '593', lang: 'es-ES' },
    { code: 'EG', name: 'Egypt', prefix: '20', lang: 'ar-EG' },
    { code: 'SV', name: 'El Salvador', prefix: '503', lang: 'es-ES' },
    { code: 'GQ', name: 'Equatorial Guinea', prefix: '240', lang: 'es-ES' },
    { code: 'ER', name: 'Eritrea', prefix: '291', lang: 'ar-SA' },
    { code: 'EE', name: 'Estonia', prefix: '372', lang: 'et-EE' },
    { code: 'SZ', name: 'Eswatini', prefix: '268', lang: 'en-GB' },
    { code: 'ET', name: 'Ethiopia', prefix: '251', lang: 'en-GB' },
    { code: 'FK', name: 'Falkland Islands', prefix: '500', lang: 'en-GB' },
    { code: 'FO', name: 'Faroe Islands', prefix: '298', lang: 'da-DK' },
    { code: 'FJ', name: 'Fiji', prefix: '679', lang: 'en-GB' },
    { code: 'FI', name: 'Finland', prefix: '358', lang: 'fi-FI' },
    { code: 'FR', name: 'France', prefix: '33', lang: 'fr-FR' },
    { code: 'GF', name: 'French Guiana', prefix: '594', lang: 'fr-FR' },
    { code: 'PF', name: 'French Polynesia', prefix: '689', lang: 'fr-FR' },
    { code: 'GA', name: 'Gabon', prefix: '241', lang: 'fr-FR' },
    { code: 'GM', name: 'Gambia', prefix: '220', lang: 'en-GB' },
    { code: 'GE', name: 'Georgia', prefix: '995', lang: 'ka-GE' },
    { code: 'DE', name: 'Germany', prefix: '49', lang: 'de-DE' },
    { code: 'GH', name: 'Ghana', prefix: '233', lang: 'en-GB' },
    { code: 'GI', name: 'Gibraltar', prefix: '350', lang: 'en-GB' },
    { code: 'GR', name: 'Greece', prefix: '30', lang: 'el-GR' },
    { code: 'GL', name: 'Greenland', prefix: '299', lang: 'da-DK' },
    { code: 'GD', name: 'Grenada', prefix: '1473', lang: 'en-GB' },
    { code: 'GP', name: 'Guadeloupe', prefix: '590', lang: 'fr-FR' },
    { code: 'GU', name: 'Guam', prefix: '1671', lang: 'en-US' },
    { code: 'GT', name: 'Guatemala', prefix: '502', lang: 'es-ES' },
    { code: 'GN', name: 'Guinea', prefix: '224', lang: 'fr-FR' },
    { code: 'GW', name: 'Guinea-Bissau', prefix: '245', lang: 'pt-PT' },
    { code: 'GY', name: 'Guyana', prefix: '592', lang: 'en-GB' },
    { code: 'HT', name: 'Haiti', prefix: '509', lang: 'fr-FR' },
    { code: 'HN', name: 'Honduras', prefix: '504', lang: 'es-ES' },
    { code: 'HK', name: 'Hong Kong', prefix: '852', lang: 'zh-HK' },
    { code: 'HU', name: 'Hungary', prefix: '36', lang: 'hu-HU' },
    { code: 'IS', name: 'Iceland', prefix: '354', lang: 'is-IS' },
    { code: 'IN', name: 'India', prefix: '91', lang: 'en-IN' },
    { code: 'ID', name: 'Indonesia', prefix: '62', lang: 'id-ID' },
    { code: 'IR', name: 'Iran', prefix: '98', lang: 'fa-IR' },
    { code: 'IQ', name: 'Iraq', prefix: '964', lang: 'ar-SA' },
    { code: 'IE', name: 'Ireland', prefix: '353', lang: 'en-IE' },
    { code: 'IL', name: 'Israel', prefix: '972', lang: 'he-IL' },
    { code: 'IT', name: 'Italy', prefix: '39', lang: 'it-IT' },
    { code: 'JM', name: 'Jamaica', prefix: '1876', lang: 'en-GB' },
    { code: 'JP', name: 'Japan', prefix: '81', lang: 'ja-JP' },
    { code: 'JO', name: 'Jordan', prefix: '962', lang: 'ar-SA' },
    { code: 'KZ', name: 'Kazakhstan', prefix: '76', lang: 'ru-RU' },
    { code: 'KE', name: 'Kenya', prefix: '254', lang: 'en-KE' },
    { code: 'KI', name: 'Kiribati', prefix: '686', lang: 'en-GB' },
    { code: 'XK', name: 'Kosovo', prefix: '383', lang: 'sq-AL' },
    { code: 'KW', name: 'Kuwait', prefix: '965', lang: 'ar-SA' },
    { code: 'KG', name: 'Kyrgyzstan', prefix: '996', lang: 'ru-RU' },
    { code: 'LA', name: 'Laos', prefix: '856', lang: 'lo-LA' },
    { code: 'LV', name: 'Latvia', prefix: '371', lang: 'lv-LV' },
    { code: 'LB', name: 'Lebanon', prefix: '961', lang: 'ar-SA' },
    { code: 'LS', name: 'Lesotho', prefix: '266', lang: 'en-GB' },
    { code: 'LR', name: 'Liberia', prefix: '231', lang: 'en-GB' },
    { code: 'LY', name: 'Libya', prefix: '218', lang: 'ar-SA' },
    { code: 'LI', name: 'Liechtenstein', prefix: '423', lang: 'de-DE' },
    { code: 'LT', name: 'Lithuania', prefix: '370', lang: 'lt-LT' },
    { code: 'LU', name: 'Luxembourg', prefix: '352', lang: 'fr-FR' },
    { code: 'MO', name: 'Macao', prefix: '853', lang: 'zh-HK' },
    { code: 'MG', name: 'Madagascar', prefix: '261', lang: 'fr-FR' },
    { code: 'MW', name: 'Malawi', prefix: '265', lang: 'en-GB' },
    { code: 'MY', name: 'Malaysia', prefix: '60', lang: 'ms-MY' },
    { code: 'MV', name: 'Maldives', prefix: '960', lang: 'en-GB' },
    { code: 'ML', name: 'Mali', prefix: '223', lang: 'fr-FR' },
    { code: 'MT', name: 'Malta', prefix: '356', lang: 'en-GB' },
    { code: 'MH', name: 'Marshall Islands', prefix: '692', lang: 'en-US' },
    { code: 'MQ', name: 'Martinique', prefix: '596', lang: 'fr-FR' },
    { code: 'MR', name: 'Mauritania', prefix: '222', lang: 'ar-SA' },
    { code: 'MU', name: 'Mauritius', prefix: '230', lang: 'en-GB' },
    { code: 'MX', name: 'Mexico', prefix: '52', lang: 'es-MX' },
    { code: 'FM', name: 'Micronesia', prefix: '691', lang: 'en-US' },
    { code: 'MD', name: 'Moldova', prefix: '373', lang: 'ro-MD' },
    { code: 'MC', name: 'Monaco', prefix: '377', lang: 'fr-FR' },
    { code: 'MN', name: 'Mongolia', prefix: '976', lang: 'mn-MN' },
    { code: 'ME', name: 'Montenegro', prefix: '382', lang: 'sr-RS' },
    { code: 'MS', name: 'Montserrat', prefix: '1664', lang: 'en-GB' },
    { code: 'MA', name: 'Morocco', prefix: '212', lang: 'ar-MA' },
    { code: 'MZ', name: 'Mozambique', prefix: '258', lang: 'pt-PT' },
    { code: 'MM', name: 'Myanmar', prefix: '95', lang: 'my-MM' },
    { code: 'NA', name: 'Namibia', prefix: '264', lang: 'en-GB' },
    { code: 'NR', name: 'Nauru', prefix: '674', lang: 'en-GB' },
    { code: 'NP', name: 'Nepal', prefix: '977', lang: 'ne-NP' },
    { code: 'NL', name: 'Netherlands', prefix: '31', lang: 'nl-NL' },
    { code: 'NC', name: 'New Caledonia', prefix: '687', lang: 'fr-FR' },
    { code: 'NZ', name: 'New Zealand', prefix: '64', lang: 'en-NZ' },
    { code: 'NI', name: 'Nicaragua', prefix: '505', lang: 'es-ES' },
    { code: 'NE', name: 'Niger', prefix: '227', lang: 'fr-FR' },
    { code: 'NG', name: 'Nigeria', prefix: '234', lang: 'en-NG' },
    { code: 'NU', name: 'Niue', prefix: '683', lang: 'en-NZ' },
    { code: 'NF', name: 'Norfolk Island', prefix: '672', lang: 'en-AU' },
    { code: 'KP', name: 'North Korea', prefix: '850', lang: 'ko-KR' },
    { code: 'MK', name: 'North Macedonia', prefix: '389', lang: 'mk-MK' },
    { code: 'MP', name: 'Northern Mariana Islands', prefix: '1670', lang: 'en-US' },
    { code: 'NO', name: 'Norway', prefix: '47', lang: 'nb-NO' },
    { code: 'OM', name: 'Oman', prefix: '968', lang: 'ar-SA' },
    { code: 'PK', name: 'Pakistan', prefix: '92', lang: 'ur-PK' },
    { code: 'PW', name: 'Palau', prefix: '680', lang: 'en-US' },
    { code: 'PS', name: 'Palestine', prefix: '970', lang: 'ar-SA' },
    { code: 'PA', name: 'Panama', prefix: '507', lang: 'es-ES' },
    { code: 'PG', name: 'Papua New Guinea', prefix: '675', lang: 'en-GB' },
    { code: 'PY', name: 'Paraguay', prefix: '595', lang: 'es-ES' },
    { code: 'PE', name: 'Peru', prefix: '51', lang: 'es-ES' },
    { code: 'PH', name: 'Philippines', prefix: '63', lang: 'en-PH' },
    { code: 'PL', name: 'Poland', prefix: '48', lang: 'pl-PL' },
    { code: 'PT', name: 'Portugal', prefix: '351', lang: 'pt-PT' },
    { code: 'PR', name: 'Puerto Rico', prefix: '1787', lang: 'es-ES' },
    { code: 'QA', name: 'Qatar', prefix: '974', lang: 'ar-SA' },
    { code: 'RE', name: 'Réunion', prefix: '262', lang: 'fr-FR' },
    { code: 'RO', name: 'Romania', prefix: '40', lang: 'ro-RO' },
    { code: 'RU', name: 'Russia', prefix: '7', lang: 'ru-RU' },
    { code: 'RW', name: 'Rwanda', prefix: '250', lang: 'fr-FR' },
    { code: 'SH', name: 'Saint Helena', prefix: '290', lang: 'en-GB' },
    { code: 'KN', name: 'Saint Kitts and Nevis', prefix: '1869', lang: 'en-GB' },
    { code: 'LC', name: 'Saint Lucia', prefix: '1758', lang: 'en-GB' },
    { code: 'PM', name: 'Saint Pierre and Miquelon', prefix: '508', lang: 'fr-FR' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', prefix: '1784', lang: 'en-GB' },
    { code: 'WS', name: 'Samoa', prefix: '685', lang: 'en-GB' },
    { code: 'SM', name: 'San Marino', prefix: '378', lang: 'it-IT' },
    { code: 'ST', name: 'São Tomé and Príncipe', prefix: '239', lang: 'pt-PT' },
    { code: 'SA', name: 'Saudi Arabia', prefix: '966', lang: 'ar-SA' },
    { code: 'SN', name: 'Senegal', prefix: '221', lang: 'fr-FR' },
    { code: 'RS', name: 'Serbia', prefix: '381', lang: 'sr-RS' },
    { code: 'SC', name: 'Seychelles', prefix: '248', lang: 'en-GB' },
    { code: 'SL', name: 'Sierra Leone', prefix: '232', lang: 'en-GB' },
    { code: 'SG', name: 'Singapore', prefix: '65', lang: 'en-SG' },
    { code: 'SX', name: 'Sint Maarten', prefix: '1721', lang: 'nl-NL' },
    { code: 'SK', name: 'Slovakia', prefix: '421', lang: 'sk-SK' },
    { code: 'SI', name: 'Slovenia', prefix: '386', lang: 'sl-SI' },
    { code: 'SB', name: 'Solomon Islands', prefix: '677', lang: 'en-GB' },
    { code: 'SO', name: 'Somalia', prefix: '252', lang: 'ar-SA' },
    { code: 'ZA', name: 'South Africa', prefix: '27', lang: 'en-ZA' },
    { code: 'KR', name: 'South Korea', prefix: '82', lang: 'ko-KR' },
    { code: 'SS', name: 'South Sudan', prefix: '211', lang: 'en-GB' },
    { code: 'ES', name: 'Spain', prefix: '34', lang: 'es-ES' },
    { code: 'LK', name: 'Sri Lanka', prefix: '94', lang: 'si-LK' },
    { code: 'SD', name: 'Sudan', prefix: '249', lang: 'ar-SA' },
    { code: 'SR', name: 'Suriname', prefix: '597', lang: 'nl-NL' },
    { code: 'SE', name: 'Sweden', prefix: '46', lang: 'sv-SE' },
    { code: 'CH', name: 'Switzerland', prefix: '41', lang: 'de-CH' },
    { code: 'SY', name: 'Syria', prefix: '963', lang: 'ar-SA' },
    { code: 'TW', name: 'Taiwan', prefix: '886', lang: 'zh-TW' },
    { code: 'TJ', name: 'Tajikistan', prefix: '992', lang: 'ru-RU' },
    { code: 'TZ', name: 'Tanzania', prefix: '255', lang: 'en-GB' },
    { code: 'TH', name: 'Thailand', prefix: '66', lang: 'th-TH' },
    { code: 'TL', name: 'Timor-Leste', prefix: '670', lang: 'pt-PT' },
    { code: 'TG', name: 'Togo', prefix: '228', lang: 'fr-FR' },
    { code: 'TK', name: 'Tokelau', prefix: '690', lang: 'en-NZ' },
    { code: 'TO', name: 'Tonga', prefix: '676', lang: 'en-GB' },
    { code: 'TT', name: 'Trinidad and Tobago', prefix: '1868', lang: 'en-GB' },
    { code: 'TN', name: 'Tunisia', prefix: '216', lang: 'ar-SA' },
    { code: 'TR', name: 'Turkey', prefix: '90', lang: 'tr-TR' },
    { code: 'TM', name: 'Turkmenistan', prefix: '993', lang: 'ru-RU' },
    { code: 'TC', name: 'Turks and Caicos', prefix: '1649', lang: 'en-GB' },
    { code: 'TV', name: 'Tuvalu', prefix: '688', lang: 'en-GB' },
    { code: 'UG', name: 'Uganda', prefix: '256', lang: 'en-GB' },
    { code: 'UA', name: 'Ukraine', prefix: '380', lang: 'uk-UA' },
    { code: 'AE', name: 'United Arab Emirates', prefix: '971', lang: 'ar-AE' },
    { code: 'GB', name: 'United Kingdom', prefix: '44', lang: 'en-GB' },
    { code: 'US', name: 'United States', prefix: '1', lang: 'en-US' },
    { code: 'VI', name: 'United States Virgin Islands', prefix: '1340', lang: 'en-US' },
    { code: 'UY', name: 'Uruguay', prefix: '598', lang: 'es-ES' },
    { code: 'UZ', name: 'Uzbekistan', prefix: '998', lang: 'uz-UZ' },
    { code: 'VU', name: 'Vanuatu', prefix: '678', lang: 'fr-FR' },
    { code: 'VA', name: 'Vatican City', prefix: '379', lang: 'it-IT' },
    { code: 'VE', name: 'Venezuela', prefix: '58', lang: 'es-ES' },
    { code: 'VN', name: 'Vietnam', prefix: '84', lang: 'vi-VN' },
    { code: 'WF', name: 'Wallis and Futuna', prefix: '681', lang: 'fr-FR' },
    { code: 'YE', name: 'Yemen', prefix: '967', lang: 'ar-SA' },
    { code: 'ZM', name: 'Zambia', prefix: '260', lang: 'en-GB' },
    { code: 'ZW', name: 'Zimbabwe', prefix: '263', lang: 'en-GB' },
];
const BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]));
export const countryByCode = (code) => BY_CODE.get(code.trim().toUpperCase());
export const prefixForCountry = (code) => countryByCode(code)?.prefix ?? '';
export const langForCountry = (code) => countryByCode(code)?.lang ?? '';
/**
 * The reverse path: from prefix to country.
 *
 * The prefix `1` is ambiguous (Canada and the United States) — the first
 * alphabetical match is returned. Callers treat the result as a suggestion: if
 * the already selected country has the requested prefix, nothing changes.
 */
export function countryForPrefix(prefix) {
    const p = prefix.replace(/^\+/, '').trim();
    return p === '' ? undefined : COUNTRIES.find((c) => c.prefix === p);
}
/** True when the chosen prefix is not the country's — a mismatch signal, not an error. */
export function prefixMismatch(country, prefix) {
    const expected = prefixForCountry(country);
    return expected !== '' && expected !== prefix.replace(/^\+/, '').trim();
}
/** The languages used by the countries above, for choosing the message. */
export const LANGUAGES = [
    { code: 'ar-AE', name: 'Arabic (UAE)' },
    { code: 'ar-DZ', name: 'Arabic (Algeria)' },
    { code: 'ar-EG', name: 'Arabic (Egypt)' },
    { code: 'ar-MA', name: 'Arabic (Morocco)' },
    { code: 'ar-SA', name: 'Arabic' },
    { code: 'az-AZ', name: 'Azerbaijani' },
    { code: 'bg-BG', name: 'Bulgarian' },
    { code: 'bn-BD', name: 'Bengali' },
    { code: 'cs-CZ', name: 'Czech' },
    { code: 'da-DK', name: 'Danish' },
    { code: 'de-AT', name: 'German (Austria)' },
    { code: 'de-CH', name: 'German (Switzerland)' },
    { code: 'de-DE', name: 'German' },
    { code: 'el-GR', name: 'Greek' },
    { code: 'en-AU', name: 'English (Australia)' },
    { code: 'en-CA', name: 'English (Canada)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'en-IE', name: 'English (Ireland)' },
    { code: 'en-IN', name: 'English (India)' },
    { code: 'en-KE', name: 'English (Kenya)' },
    { code: 'en-NG', name: 'English (Nigeria)' },
    { code: 'en-NZ', name: 'English (New Zealand)' },
    { code: 'en-PH', name: 'English (Philippines)' },
    { code: 'en-SG', name: 'English (Singapore)' },
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-ZA', name: 'English (South Africa)' },
    { code: 'es-AR', name: 'Spanish (Argentina)' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'es-MX', name: 'Spanish (Mexico)' },
    { code: 'et-EE', name: 'Estonian' },
    { code: 'fa-IR', name: 'Persian' },
    { code: 'fi-FI', name: 'Finnish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'he-IL', name: 'Hebrew' },
    { code: 'hr-HR', name: 'Croatian' },
    { code: 'hu-HU', name: 'Hungarian' },
    { code: 'hy-AM', name: 'Armenian' },
    { code: 'id-ID', name: 'Indonesian' },
    { code: 'is-IS', name: 'Icelandic' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ka-GE', name: 'Georgian' },
    { code: 'km-KH', name: 'Khmer' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'lo-LA', name: 'Lao' },
    { code: 'lt-LT', name: 'Lithuanian' },
    { code: 'lv-LV', name: 'Latvian' },
    { code: 'mk-MK', name: 'Macedonian' },
    { code: 'mn-MN', name: 'Mongolian' },
    { code: 'ms-MY', name: 'Malay' },
    { code: 'my-MM', name: 'Burmese' },
    { code: 'nb-NO', name: 'Norwegian' },
    { code: 'ne-NP', name: 'Nepali' },
    { code: 'nl-BE', name: 'Dutch (Belgium)' },
    { code: 'nl-NL', name: 'Dutch' },
    { code: 'pl-PL', name: 'Polish' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)' },
    { code: 'pt-PT', name: 'Portuguese' },
    { code: 'ro-MD', name: 'Romanian (Moldova)' },
    { code: 'ro-RO', name: 'Romanian' },
    { code: 'ru-RU', name: 'Russian' },
    { code: 'si-LK', name: 'Sinhala' },
    { code: 'sk-SK', name: 'Slovak' },
    { code: 'sl-SI', name: 'Slovenian' },
    { code: 'sq-AL', name: 'Albanian' },
    { code: 'sr-RS', name: 'Serbian' },
    { code: 'sv-SE', name: 'Swedish' },
    { code: 'th-TH', name: 'Thai' },
    { code: 'tr-TR', name: 'Turkish' },
    { code: 'uk-UA', name: 'Ukrainian' },
    { code: 'ur-PK', name: 'Urdu' },
    { code: 'uz-UZ', name: 'Uzbek' },
    { code: 'vi-VN', name: 'Vietnamese' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
    { code: 'zh-HK', name: 'Chinese (Hong Kong)' },
    { code: 'zh-TW', name: 'Chinese (Traditional)' },
];
//# sourceMappingURL=countries.js.map